import { Injectable } from '@angular/core';
import { from, Subject, Subscription } from 'rxjs';
import { ErrorStreamChange } from '../models/$error.model';
import { AccountsService } from "./accounts.service";
import { takeUntil, tap } from "rxjs/operators";
import { LogService } from "./log.service";
import { Contact, FirebaseAccountDocument, ContactByAccountIdAndEmail, contactsFeature, contactsActions } from '../store/contacts.era';
import { Store, select } from '@ngrx/store';
import { WFWorker } from '../store/events.era';
import { OnReset, Transitions } from './transition.service.helpers';
import { WorkerWithProfile } from 'globals/store/workforce.data';
import { transformObservableToSinglePromise } from '../helpers/angular-helpers';
import { userFeature } from '../store/user.era';
import { Account } from 'globals/store/accounts.data';
import { copyObjectWithProps } from 'globals/helpers/helpers';
import { doc, docData, Firestore, onSnapshot, updateDoc } from '@angular/fire/firestore';

@Injectable({ providedIn: 'root' })
export class ContactsService implements OnReset {

  private _file = 'contacts.service.ts';
  public $errors: Subject<ErrorStreamChange> = new Subject();
  private _prev_accountIds = [];

  // private preferredFields = ['uid', 'email', 'firstName', 'lastName', 'company', 'phone', 'photoURL', 'title'];
  private contactDtoFields = new Set(['uid', 'email', 'firstName', 'lastName', 'company', 'phone', 'photoURL', 'title'])
  private currentUserId
  private accountIds = new Set()
  private $resetSubscriptions:Subject<boolean> = new Subject();
  private $eachSubscription:Subject<boolean>[] = [];
  private firestoreContactsSubscriptions: Subscription[] = []

  private reset() {
    this.firestoreContactsSubscriptions.forEach(s => s.unsubscribe())
    this.firestoreContactsSubscriptions = []
    this.$resetSubscriptions.next(true)
    this.accountIds = new Set()
  }

  onReset(resetType, value = null) {
    // console.log("On reset called in contacts service", { resetType, value })
    switch (resetType) {
      case Transitions.OnEnterUser:
        this.currentUserId = value.uid
        break;
      case Transitions.OnExitUser:
        // console.log("On exit user called in contacts service")
        this.reset()
        this.currentUserId = null
        break;
      case Transitions.OnEnterEvent:
        break;
      case Transitions.OnExitEvent:
        break;
      default:
        break;
    }
  }

  constructor(
    public account: AccountsService,
    // public notifications: NotificationsService,
    private log: LogService,
    private db: Firestore,
    public store: Store,
    ) {

    this.store.select(userFeature.selectUser).subscribe(({ accounts: accountIds }) => {
      if (!accountIds || (!accountIds.length && !this._prev_accountIds.length)) return // (user.accounts fires with an empty list on load)
      if(!accountIds || !accountIds.length) accountIds = []; // so removed accounts can be checked for
      this._prev_accountIds.filter(id=>!accountIds.includes(id)).forEach(removed_id=>this.$eachSubscription[removed_id].next(true))
      this._prev_accountIds = accountIds
      accountIds.filter(id => !this.accountIds.has(id)).forEach(id => {
        this.accountIds.add(id)
        this.$eachSubscription[id] = new Subject()
        const docRef = doc(this.db, 'contacts/' + id)
        const subscription: Subscription = from(docData(docRef)).subscribe({next: (snapshot) => {
          console.log('contacts snapshot for account ', id, snapshot)
          if(!snapshot) return;
          const contacts: Contact[]  = snapshot.contacts as unknown as Contact[]
          const {accountId} = snapshot
          this.store.dispatch(contactsActions.loadContactsByAccountId(accountId, contacts)) // Need to implement add a way to delete accountIds from accountIdListByEmail in the store
          },
          error: response=> {  
            if (response.code === 'permission-denied') { // currently, this error WILL occur when someone removes themselves from an account.  Rather than trying to fix the subscription logic for that, I am minimizing the error here so that Tony doesn't immediately open up another bug report
              console.error(response)
            } else {
              this.log.error({file:this._file, function:'constructor', response, error: new Error()})
            } 
          }})
        
        
        this.firestoreContactsSubscriptions.push(subscription)
      })
    })

  }

  addContact(contact: WorkerWithProfile, accountId: string, msg?: string) {
    // why not just use the addMultiple method?  Does any of the below actually do anything necessary / useful?  I am changing this to use my reworked version of addMultiple for now
    this.addMultipleToContacts([contact], [accountId]).then(_ => console.log(`STILL NEED TO DO SOMETHING WITH THE ON SUCCESS MESSAGE FOR contacts.service.addContact`, {msg})) // perhaps send msg somewhere

  }
  removeContact(accountId, email_or_uid) {
    // this is currently not used anywhere.  I am leaving the old code here for the moment, commented out
    console.log('removeContact not reimplemented yet (since it wasn\'t in use before)')
  }

  async addMultipleToContacts(workers: WFWorker[], Ids: string[]) {

    const contactByAccountIdAndEmail = await transformObservableToSinglePromise<ContactByAccountIdAndEmail>(this.store.pipe(select(contactsFeature.selectContactByAccountIdAndEmail)))
    const upsertPromises = Ids.map(accountId => {
      const oldAccountContactsObject = contactByAccountIdAndEmail[accountId] || {}

      workers.filter(w => !oldAccountContactsObject[w.email]).forEach(worker =>
        oldAccountContactsObject[worker.email] = copyObjectWithProps<Contact>(this.contactDtoFields, worker as Contact))

      const contacts = Object.values(oldAccountContactsObject)

      // optional to load the new ones right away here
      this.store.dispatch(contactsActions.loadContactsByAccountId(accountId, contacts ))

      return updateDoc(doc(this.db, 'contacts/' + accountId), { accountId, contacts })
    })
    return Promise.all(upsertPromises)
  }
}
